import { useState } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useRPLStats,
  useTrainingSessions,
  useTrainingTypes,
  getTrainingStatusLabel,
  getTrainingStatusColor,
} from '@/hooks/useRPL';
import { TrainingSessionFormDialog } from '@/components/RPL/Training/TrainingSessionFormDialog';
import { CertificationExpiryList } from '@/components/RPL/Training/CertificationExpiryList';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  GraduationCap,
  ArrowLeft,
  Plus,
  Calendar,
  Users,
  Clock,
  Award,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { TrainingSession } from '@/types/rpl';

export default function RPLTraining() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { data: stats } = useRPLStats();
  const { data: sessions, isLoading } = useTrainingSessions({ upcoming: true });
  const { data: trainingTypes } = useTrainingTypes();
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<TrainingSession | null>(null);

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const handleEditSession = (session: TrainingSession) => {
    setSelectedSession(session);
    setSessionDialogOpen(true);
  };

  const handleNewSession = () => {
    setSelectedSession(null);
    setSessionDialogOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/rpl')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent flex items-center gap-2">
                <GraduationCap className="w-8 h-8 text-blue-500" />
                Formaciones de Seguridad
              </h1>
              <p className="text-muted-foreground mt-1">
                Gestion de formaciones y certificaciones
              </p>
            </div>
          </div>
          {canManage && (
            <Button onClick={handleNewSession}>
              <Plus className="w-4 h-4 mr-2" />
              Nueva Sesion
            </Button>
          )}
        </div>

        {/* KPIs */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Formaciones programadas</p>
                  <p className="text-2xl font-bold text-blue-500">{stats?.upcoming_trainings || 0}</p>
                </div>
                <Calendar className="w-5 h-5 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Tipos de formacion</p>
                  <p className="text-2xl font-bold">{trainingTypes?.length || 0}</p>
                </div>
                <GraduationCap className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Certificaciones por vencer</p>
                  <p className={`text-2xl font-bold ${stats?.certifications_expiring_soon ? 'text-yellow-500' : 'text-green-500'}`}>
                    {stats?.certifications_expiring_soon || 0}
                  </p>
                </div>
                {stats?.certifications_expiring_soon ? (
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Award className="w-5 h-5 text-green-500" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="sessions">
          <TabsList>
            <TabsTrigger value="sessions">Sesiones Programadas</TabsTrigger>
            <TabsTrigger value="certifications">Certificaciones por Vencer</TabsTrigger>
            <TabsTrigger value="types">Tipos de Formacion</TabsTrigger>
          </TabsList>

          <TabsContent value="sessions" className="mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : sessions?.length === 0 ? (
              <Card className="p-8 text-center">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No hay sesiones programadas</p>
                {canManage && (
                  <Button className="mt-4" onClick={handleNewSession}>
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Primera Sesion
                  </Button>
                )}
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {sessions?.map((session) => (
                  <Card
                    key={session.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleEditSession(session)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">{session.title}</CardTitle>
                        <Badge className={getTrainingStatusColor(session.status)}>
                          {getTrainingStatusLabel(session.status)}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(session.scheduled_date), 'EEEE, d MMMM yyyy', { locale: es })}
                      </div>

                      {session.start_time && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          {session.start_time}
                          {session.end_time && ` - ${session.end_time}`}
                        </div>
                      )}

                      {session.instructor && (
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          {session.instructor}
                        </div>
                      )}

                      {session.location && (
                        <p className="text-sm text-muted-foreground">{session.location}</p>
                      )}

                      {session.training_type && (
                        <Badge variant="outline" className="mt-2">
                          {session.training_type.name}
                          {session.training_type.is_mandatory && ' (Obligatoria)'}
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="certifications" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Certificaciones Proximas a Vencer</CardTitle>
              </CardHeader>
              <CardContent>
                <CertificationExpiryList />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="types" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Tipos de Formacion Disponibles</CardTitle>
              </CardHeader>
              <CardContent>
                {trainingTypes?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    No hay tipos de formacion configurados
                  </p>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {trainingTypes?.map((type) => (
                      <div key={type.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{type.name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            {type.duration_hours && <span>{type.duration_hours}h</span>}
                            {type.validity_months && <span>Valido {type.validity_months} meses</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {type.is_mandatory && (
                            <Badge variant="destructive">Obligatoria</Badge>
                          )}
                          <Badge variant={type.is_active ? 'default' : 'secondary'}>
                            {type.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <TrainingSessionFormDialog
        open={sessionDialogOpen}
        onOpenChange={setSessionDialogOpen}
        session={selectedSession}
      />
    </DashboardLayout>
  );
}
